<template>
  <section>
    <div v-if="loading && items.length === 0" class="muted">Loading...</div>
    <div v-else-if="items.length === 0" class="muted">No activity yet.</div>
    <div v-else class="altTimeline">
      <div class="altTimelineLine" />
      <div
        v-for="(item, idx) in items"
        :key="String(item.id || idx)"
        class="altEntry"
        :class="idx % 2 === 0 ? 'altLeft' : 'altRight'"
      >
        <div class="altDot" :style="{ background: timelineIconColor(item) }" />
        <button type="button" class="altCard altCardButton" @click="onItemClick(item)">
          <div class="altCardHead">
            <span class="altIcon" :style="{ color: timelineIconColor(item) }">
              <AppIcon :name="timelineIconKey(item)" :size="14" />
            </span>
            <span class="altType">{{ activityTypeTitle(String(item.type || ''), item) }}</span>
            <span class="altDate">{{ formatDateShort(item.created_at) }}</span>
          </div>
          <div class="altCardTitle">{{ timelineItemTitle(item) }}</div>
          <div v-if="timelineItemSubtitle(item)" class="altCardSub">{{ timelineItemSubtitle(item) }}</div>
          <div class="altCardTime">{{ formatTime(item.created_at) }}</div>
        </button>
      </div>
      <div v-if="nextOffset != null" class="altLoadMore">
        <button type="button" class="n-pill" @click="loadMore">Load more</button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import AppIcon from '../../components/AppIcon.vue'
import { apiFetch } from '../../services/api'
import {
  formatTime,
  formatDateShort,
  timelineIconColor,
  timelineIconKey,
  timelineItemTitle,
  timelineItemSubtitle,
  activityTypeTitle,
} from '../../services/timelineFormat'
import { useActivityDialog } from '../composables/useActivityDialog'

export type ActivityItem = {
  id: string
  account_id?: string
  type: string
  created_at: string
  run_id?: string | null
  data: Record<string, any>
}

const props = defineProps<{
  accountId: string
  contactId?: string | null
}>()

const items = ref<ActivityItem[]>([])
const loading = ref(false)
const nextOffset = ref<number | null>(null)

const { openActivityDialog } = useActivityDialog({ onUpdated: () => void load(0) })

async function load(offset = 0) {
  const aid = props.accountId
  if (!aid) { items.value = []; return }
  loading.value = true
  try {
    const params = new URLSearchParams({ limit: '20', offset: String(offset) })
    if (props.contactId) params.set('contact_id', props.contactId)
    const resp = await apiFetch(`/accounts/${aid}/activity?${params}`)
    const rows: ActivityItem[] = Array.isArray(resp?.items) ? resp.items : []
    if (offset === 0) {
      items.value = rows
    } else {
      items.value = [...items.value, ...rows]
    }
    nextOffset.value = rows.length >= 20 ? offset + rows.length : null
  } catch {
    if (offset === 0) items.value = []
  } finally {
    loading.value = false
  }
}

function loadMore() {
  if (nextOffset.value != null) void load(nextOffset.value)
}

function onItemClick(item: ActivityItem) {
  void openActivityDialog(item)
}

watch(() => [props.accountId, props.contactId], () => void load(0))
onMounted(() => void load(0))
</script>

<style scoped>
.muted {
  font-size: 13px;
  color: var(--color-text-tertiary, #a3a3a3);
}

.altTimeline {
  position: relative;
  padding: 0 0 var(--space-5, 20px);
}

.altTimelineLine {
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--color-border, #e5e2dc);
  transform: translateX(-50%);
}

.altEntry {
  position: relative;
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
}

.altEntry.altLeft {
  justify-content: flex-start;
  padding-right: calc(50% + 20px);
}

.altEntry.altRight {
  justify-content: flex-end;
  padding-left: calc(50% + 20px);
}

.altDot {
  position: absolute;
  left: 50%;
  top: 14px;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  transform: translateX(-50%);
  z-index: 1;
  border: 2px solid var(--color-bg-app, #fff);
}

.altCard {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 14px;
  border-radius: var(--radius-md, 8px);
  background: var(--color-bg-surface, #faf9f6);
  border: 1px solid var(--color-border, #e5e2dc);
  width: 100%;
  transition: box-shadow 150ms ease;
}
.altCard:hover {
  box-shadow: var(--shadow-neu-out-cream-sm, 0 1px 4px rgba(0,0,0,.05));
}

.altCardButton {
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.altCardHead {
  display: flex;
  align-items: center;
  gap: 6px;
}

.altIcon {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.altType {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary, #a3a3a3);
}

.altDate {
  margin-left: auto;
  font-size: 11px;
  color: var(--color-text-tertiary, #a3a3a3);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.altCardTitle {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary, #1a1a1a);
  line-height: 1.35;
}

.altCardSub {
  font-size: 12px;
  color: var(--color-text-tertiary, #a3a3a3);
  line-height: 1.35;
}

.altCardTime {
  font-size: 11px;
  color: var(--color-text-tertiary, #a3a3a3);
  font-variant-numeric: tabular-nums;
}

.altLoadMore {
  display: flex;
  justify-content: center;
  padding-top: var(--space-3, 12px);
}
</style>
